import i18n from "i18next";

// ---------------------------------------------------------------------------
// Bytes
// ---------------------------------------------------------------------------

/**
 * Format bytes into a human-readable string.
 * Accepts both `number` and `string` (some API fields return string).
 * @example formatBytes(1024) → "1.00 KB"
 * @example formatBytes("1073741824") → "1 GB"
 */
export function formatBytes(bytesVal: number | string, decimals = 2): string {
  const bytes = typeof bytesVal === "string" ? Number(bytesVal) : bytesVal;
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

/**
 * Format a date value to a locale string.
 *
 * Accepts:
 * - Unix timestamp in milliseconds (`number`)
 * - ISO 8601 string (`string`)
 * - `null` / `undefined` → returns the provided fallback (default `""`)
 */
export function formatDate(
  value: number | string | null | undefined,
  options?: Intl.DateTimeFormatOptions,
  fallback = "",
): string {
  if (value === null || value === undefined) return fallback;

  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };

  try {
    const date = typeof value === "number" ? new Date(value) : new Date(value);
    if (isNaN(date.getTime())) return fallback;
    const locale = i18n.resolvedLanguage || i18n.language || undefined;
    return date.toLocaleDateString(locale, options ?? defaultOptions);
  } catch {
    return typeof value === "string" ? value : fallback;
  }
}

export function defaultPasskeyEnrolName(now = Date.now()): string {
  const stamped = formatDate(now, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return (stamped || new Date(now).toISOString()).slice(0, 100);
}

/**
 * Format a date with long month name (e.g., "January 15, 2026").
 */
export function formatDateLong(value: number | string | null | undefined, fallback = ""): string {
  return formatDate(value, { year: "numeric", month: "long", day: "numeric" }, fallback);
}

/**
 * Format a Unix timestamp (in milliseconds) to a relative time string.
 * @example formatRelativeTime(Date.now() - 60000) → "1 minute ago"
 */
export function formatRelativeTime(unixMs: number): string {
  const seconds = Math.floor((Date.now() - unixMs) / 1000);
  const locale = i18n.resolvedLanguage || i18n.language || "en";

  if (seconds < 60) {
    return locale.startsWith("es") ? "ahora mismo" : "just now";
  }

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "always" });

  const intervals: { divisor: number; unit: Intl.RelativeTimeFormatUnit }[] = [
    { divisor: 31536000, unit: "year" },
    { divisor: 2592000, unit: "month" },
    { divisor: 86400, unit: "day" },
    { divisor: 3600, unit: "hour" },
    { divisor: 60, unit: "minute" },
  ];

  for (const { divisor, unit } of intervals) {
    const count = Math.floor(seconds / divisor);
    if (count >= 1) {
      return rtf.format(-count, unit);
    }
  }

  return locale.startsWith("es") ? "ahora mismo" : "just now";
}
